/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import * as analytics from '@adsk/acs-web-analytics';
import { useCurrentProjectId, useCurrentToolId } from '@adsk/alloy-provider';

import keyBy from 'lodash/keyBy';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import {
  useTable,
  TableRow,
  TableCell,
  TableHeader,
  TableHeaderGroup,
  LoadMoreDataFooter,
} from '@adsk/alloy-react-table';
import { useReactTable, getCoreRowModel, Row, Updater, SortingState } from '@tanstack/react-table';

import { CSSProp } from 'styled-components';
import {
  ENTITIES_MAPPER_KEY,
  TABLE_COLUMN_ID_KEY,
  ORDERS,
  DATAVIEW_PROPS,
  TEST_KEY_PREFIX,
} from './helpers/constants';
import { RFICustomDataViewProps } from './helpers/types';
import { useTableColumns, useVirtualTable } from './hooks';
import { getSdk } from '../helpers/getSdk';
import { AssigneeType, CustomAttribute, Rfi, RfiStatus } from '../../../sdk';
import { getRfisListTrackEvent, translate } from '../../../helpers';
import { MaintenanceMode } from '../../../components';
import { STYLES } from './helpers/styles';
import { renderSelectionCell, renderSelectionHeader } from './helpers/tableHelper';
import { createRowSelection } from './helpers/utils';

/**
 * A component for showing custom data view.
 * Used to show the search result, in any way the plugin wish to.
 * Take a look at `EntityPickerModalListContent.js` for reference.
 *
 * @param {object} plugin - the current plugin
 * @param {Array<object>} entities - list of entities as returned from `queryEntities`
 * @param {func} onLoadMore - handler to fetch next page, as retrieved from `queryEntities`.
 * @param {Array<string>} disabledIds - array of IDs we want to mark as disabled.
 * @param {Array<string>} selectedIds - array of selected entity IDs.
 * @param {boolean} multiple - whether the component is multi selectable.
 * @param {func} onChange - handler for changing selection state. Shape: ({entity, checked}) => *
 * @param {func} setSelection - handler for add selection entities. Shape: (selectedNodes) => *
 * @param {boolean} loading - whether we're loading something from BE.
 * @param {boolean} error - whether we had errors loading something from BE.
 * @param {func} onTryAgain - callback to fetch data again.
 * @param {object} filters - the currently applied filters.
 * @param {func} addEntitiesToCache - A handler to add entities to cache in case the view fetch more entities. Shape: () => {entities, domain, type}
 * @param {object} dataViewProps - Additional props passed to CustomDataView.
 * @param {string} projectId - Current project ID
 * @param {func} onUpdate - callback to update an entity
 * @param {object} sort - Current sort options
 * @param {func} setSort - Callback for setting sort options.
 */

const RFICustomDataView = ({
  entities = [],
  onLoadMore,
  disabledIds = [],
  selectedIds = [],
  multiple,
  onChange,
  setSelection,
  loading,
  error,
  onTryAgain,
  setSort,
  sort,
  dataViewProps,
}: RFICustomDataViewProps) => {
  const projectId = useCurrentProjectId();
  const [customAttributes, setCustomAttributes] = useState<CustomAttribute[]>([]);
  const currentToolId = useCurrentToolId();
  // If the source entity is a rfi, disable it in the table
  const [disabledIdsWithSource, selectedIdsWithSource] = useMemo(
    () =>
      dataViewProps?.sourceEntityId &&
      dataViewProps?.sourceEntityType === DATAVIEW_PROPS.SOURCE_ENTITY_TYPE &&
      dataViewProps?.sourceEntityDomain === DATAVIEW_PROPS.SOURCE_ENTITY_DOMAIN
        ? [
            [...disabledIds, dataViewProps.sourceEntityId],
            [...selectedIds, dataViewProps.sourceEntityId],
          ]
        : [disabledIds, selectedIds],
    [disabledIds, selectedIds, dataViewProps],
  );

  useEffect(() => {
    const { eventName, properties } = getRfisListTrackEvent(currentToolId, 'unknown');
    analytics.track(eventName, properties);
  }, [currentToolId]);

  useEffect(() => {
    if (!projectId) {
      return;
    }
    const sdk = getSdk(projectId);
    async function fetchCustomAttributes() {
      const { results } = await sdk.CustomAttributes.getAll({
        filterStatus: ['active', 'inactive'],
        limit: 200,
      });
      setCustomAttributes(results);
    }
    fetchCustomAttributes();
  }, [projectId]);

  const dataTableColumns = useTableColumns(customAttributes);

  const dataByKey = useMemo(() => keyBy(entities, ENTITIES_MAPPER_KEY), [entities]);

  const columns = useMemo(
    () => [
      ...(multiple
        ? [
            {
              id: TABLE_COLUMN_ID_KEY.SELECT,
              size: 40,
              enableResizing: false,
              header: renderSelectionHeader({
                setSelection,
                entities,
                disabledIds: disabledIdsWithSource,
              }),
              cell: renderSelectionCell,
            },
          ]
        : []),
      ...dataTableColumns,
    ],
    [dataTableColumns, entities, setSelection, multiple],
  );

  const sorting = useMemo(
    () =>
      sort?.field
        ? [
            {
              id: sort.field.toString(),
              desc: sort?.order === ORDERS.DESCENDING,
            },
          ]
        : [],
    [sort],
  );

  const rowSelection = useMemo(
    () => createRowSelection(selectedIdsWithSource),
    [selectedIdsWithSource],
  );

  const handleSortChange = (updaterOrValue: Updater<SortingState>) => {
    const sortValue =
      typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (sortValue?.[0]) {
      const { id, desc } = sortValue[0];
      setSort?.({
        field: id,
        order: desc ? ORDERS.DESCENDING : ORDERS.ASCENDING,
        columnName: id,
      });
    }
  };

  const table = useReactTable({
    columns: columns as [],
    data: entities,
    getRowId: (row: Rfi) => row?.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: (row: Row<Rfi>) => !disabledIdsWithSource?.includes(row.id),
    enableMultiRowSelection: multiple,
    enableColumnResizing: true,
    enableSortingRemoval: false,
    columnResizeMode: 'onChange',
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange: handleSortChange,
  });

  const { getTableRowProps, getTableCellProps, getTableHeaderGroupProps, getTableHeaderProps } =
    useTable({
      table,
    });

  const { getCenterTotalSize, getRowModel, getHeaderGroups } = table;

  const { rows } = getRowModel();

  const { handleScroll, virtualRows, layouts, tableRef } = useVirtualTable({
    onLoadMore,
    rowsLength: rows?.length,
    loading,
    error: !!error,
  });

  const { paddingTop, paddingBottom } = layouts;

  const handleRowSelection = ({ getCanSelect, getIsSelected, id }: Row<Rfi>) => {
    if (!getCanSelect?.()) return;
    const selectedRow = dataByKey[id] as Rfi;
    if (multiple) {
      onChange({
        entity: selectedRow,
        checked: !getIsSelected?.(),
      });
    } else {
      setSelection(selectedIds.includes(id) ? [] : [selectedRow]);
    }
  };

  return (
    <MaintenanceMode>
      <div css={STYLES.TableContainer} onScroll={handleScroll} ref={tableRef}>
        <table css={STYLES.TableStyle(getCenterTotalSize()) as CSSProp}>
          <thead css={STYLES.StickyHeader as CSSProp}>
            {getHeaderGroups()?.map((headerGroup) => (
              <TableHeaderGroup key={headerGroup.id} {...getTableHeaderGroupProps(headerGroup)}>
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    {...getTableHeaderProps(header)}
                    style={STYLES.HeaderOverFlowText}
                  />
                ))}
              </TableHeaderGroup>
            ))}
          </thead>
          <tbody>
            {paddingTop > 0 && <TableRow style={{ height: `${paddingTop}px` }} />}
            {virtualRows.map(({ index }) => {
              const row = rows[index];
              const { getVisibleCells, id, getCanSelect } = row;
              return (
                <TableRow
                  key={id}
                  {...getTableRowProps(row)}
                  onClick={() => handleRowSelection(row)}
                  disabled={!getCanSelect()}
                  data-testid={`${TEST_KEY_PREFIX.TABLE_ROW}${id}`}
                >
                  {getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      {...getTableCellProps(cell)}
                      data-testid={`${TEST_KEY_PREFIX.TABLE_CELL}${cell.id}`}
                    />
                  ))}
                </TableRow>
              );
            })}
            {paddingBottom > 0 && <TableRow style={{ height: `${paddingBottom}px` }} />}
          </tbody>
        </table>
        {(loading || error) && (
          <LoadMoreDataFooter
            isLoading={loading}
            onLoad={async () => onLoadMore?.()}
            hasError={!!error}
            onRetry={async () => onTryAgain?.()}
            style={STYLES.LoadMoreDataRow}
            renderLoading={() => (
              <React.Fragment>
                <ProgressRing size={ProgressRing.SIZES.SMALL} />
                <span css={STYLES.LoadMoreText}>{translate('rpPlugin.FETCHING_MORE_RESULTS')}</span>
              </React.Fragment>
            )}
          />
        )}
      </div>
    </MaintenanceMode>
  );
};

RFICustomDataView.propTypes = {
  /**
   * Whether we're loading something from BE.
   */
  loading: PropTypes.bool,
  /**
   * Whether had error fetching data from BE.
   */
  error: PropTypes.bool,
  /**
   * Callback to fetch data again.
   */
  onTryAgain: PropTypes.func,
  /**
   * Load the next page of entities.
   */
  onLoadMore: PropTypes.func,
  /**
   * IDs of selected entities.
   */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * IDs of entities we want to mark as disabled.
   */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether the component is multi selectable.
   */
  multiple: PropTypes.bool,
  /**
   * List of RFI (look in rfis/sdk/types/RFI.ts).
   */
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      customIdentifier: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      status: PropTypes.oneOf(Object.values(RfiStatus)).isRequired,
      assignedTo: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          type: PropTypes.oneOf(Object.values(AssigneeType)),
        }),
      ),
      dueDate: PropTypes.string,
      locationDescription: PropTypes.string,
      priority: PropTypes.oneOf(['Low', 'Normal', 'High']),
      createdBy: PropTypes.string.isRequired,
      locations: PropTypes.arrayOf(PropTypes.string),
    }),
  ),
  /**
   * Callback to set Selections.
   * Handler is of shape: (selectedNodes) => *
   */
  setSelection: PropTypes.func,
  /**
   * Callback to change selection.
   */
  onChange: PropTypes.func,
  /**
   * The currently applied filters.
   */
  filters: PropTypes.object,
  /**
   * Callback for setting sort options.
   */
  setSort: PropTypes.func,
  /**
   * Current sort options
   */
  sort: PropTypes.object,
  /**
   * Custom props for data views
   */
  dataViewProps: PropTypes.shape({
    dataFilter: PropTypes.func,
    sourceEntityId: PropTypes.string,
    sourceEntityDomain: PropTypes.string,
    sourceEntityType: PropTypes.string,
  }),
};

export default RFICustomDataView;
